import {
  LayoutDashboard, Users, UserRound, UserRoundX, BadgeInfo, ClipboardList, ClipboardCheck,
  Wallet, CalendarClock, Layers, Component, TableProperties, Banknote,
  HandCoins, Landmark, Receipt, FileSpreadsheet, FileBarChart2, ScrollText, NotebookPen,
  GraduationCap, BookOpen, FolderTree, FileCheck, FileText, IdCard,
  Clock, CalendarCheck, SquareCheckBig, Timer, AlertCircle, Coffee,
  Building2, Building, Network, MapPin, Briefcase, BadgeCheck, KeyRound, Grid2x2,
  SlidersHorizontal, TrendingUp, Award, Star, BookOpenText,
  UserCog, ShieldCheck, ShieldEllipsis, KeySquare, UsersRound, Wrench,
  Copy, LogOut, ChevronRight, DoorOpen, ListChecks,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { canAccessOrganizationList, hasPermissionAction } from "../../lib/organizationPermissions";
import { routes as permissionRoutes } from "../../Configurations/PermissionConfigrations";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";

/* ── Section label map ───────────────────────── */
const SECTION_BEFORE = {
  Dashboard: "Overview",
  "Employee Management": "People",
  "Intern Management": "People",
  "Document Records": "People",
  "Organization SetUp": "Organization",
  "Organization Configuration": "Organization",
  Recruitment: "Operations",
  "Payroll Management": "Operations",
  "Time & Attendance": "Operations",
  "Leave Management": "Operations",
  "Salary Updates": "Operations",
  "Skill management": "Operations",
  "Learning resources": "Operations",
  "User management": "Configuration",
  "Tools & Utilities": "Configuration",
};

/* ── Animated collapse ───────────────────────── */
function NavChildren({ open, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(open ? "auto" : 0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      const target = el.scrollHeight;
      setH(target);
      const t = setTimeout(() => setH("auto"), 230);
      return () => clearTimeout(t);
    } else {
      setH(el.scrollHeight);
      requestAnimationFrame(() => requestAnimationFrame(() => setH(0)));
    }
  }, [open]);
  return (
    <div
      className="ems-nav-children"
      ref={ref}
      style={{ height: h === "auto" ? "auto" : h + "px" }}
    >
      <div style={{ paddingBottom: 4 }}>{children}</div>
    </div>
  );
}

/* ── SideBar ─────────────────────────────────── */
export default function SideBar({ Permission, collapsed = false, onRequestExpand }) {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          logout();
          localStorage.removeItem("token");

          Swal.fire({
            title: "Logged out!",
            text: "You have been logged out.",
            icon: "success",
            confirmButtonColor: "#0891B2",
          });
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          Swal.fire({
            title: "Error",
            text: "Logout failed. Please try again.",
            icon: "error",
            confirmButtonColor: "#0891B2",
          });
        }
      }
    });
  };

  const sidebarOptions = [
    { kind: "header", title: "" },
    {
      heading: "Dashboard",
      route: "/",
      title: "Dashboard",
      icon: <LayoutDashboard size={19} strokeWidth={2} />,
      PermissionKey: "",
    },
    { kind: "divider" },
    {
      heading: "Employee Management",
      title: "Employee Management",
      icon: <Users size={19} strokeWidth={2} />,
      children: [
        {
          route: "/organization/employee/employee-details",
          title: "Employees",
          icon: <UserRound size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_VIEW_ALL",
        },

        {
          route: "/organization/employee/employee-exits",
          title: "Employee Exits",
          icon: <UserRoundX size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_EXIT_VIEW_ALL",
        },
        {
          route: "/organization/employee/records",
          title: "Employment Records",
          icon: <ClipboardList size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_RECORD_ALL",
        },
        {
          route: "/organization/employee/functional-role",
          title: "Employee Functional Roles",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_FUNCTIONAL_ROLE_VIEW_ALL",
        },
        {
          route: "/employment/employee-stages",
          title: "Employment Stages",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_STAGE_VIEW_ALL",
        },
      ],
    },

    {
      heading: "Intern Management",
      title: "Intern Management",
      icon: <GraduationCap size={19} strokeWidth={2} />,
      children: [
        {
          route: "/intern/internship/types",
          title: "Internship Types",
          icon: <GraduationCap size={16} strokeWidth={2} />,
          PermissionKey: "INTERNSHIP_TYPE_ALL",
        },

        {
          route: "/intern/internship/status",
          title: "Internship Statuses",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "INTERNSHIP_STATUS_ALL",
        },
        {
          route: "/organization/intern/intern-details",
          title: "Interns",
          icon: <GraduationCap size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_ALL",
        },
        {
          route: "/organization/intern/intern-exit",
          title: "Intern Exits",
          icon: <UserRoundX size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_ALL",
        },
        {
          route: "/organization/intern/intern-leaves",
          title: "Intern Leaves",
          icon: <NotebookPen size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_LEAVE",
        },
        // {
        //   route: "/intern/document/types",
        //   title: "Document Types",
        //   icon: <FolderTree size={16} strokeWidth={2} />,
        //   PermissionKey: "INTERN_DOCUMENT",
        // },
        {
          route: "/organization/intern/intern-stipend",
          title: "Intern Stipend",
          icon: <HandCoins size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_STIPEND",
        },

        {
          route: "/intern/attendance/time-logs",
          title: "Intern Attendance Time logs",
          icon: <Timer size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_TIME_LOG_ALL",
        },
        {
          route: "/intern/attendance/records",
          title: "Intern Attendance Records",
          icon: <SquareCheckBig size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_ATTENDANCE_RECORD_ALL",
        },
        {
          route: "/intern/attendance/monthly-report",
          title: "Intern Monthly Attendance",
          icon: <FileSpreadsheet size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_ATTENDANCE_RECORD_ALL",
        },
        {
          route: "/intern/certificates",
          title: "Intern Certificates",
          icon: <Award size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_CERTIFICATE_ALL",
        },
        {
          route: "/intern/intern-stages",
          title: "Internship Stages",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_STAGES_ALL",
        },
        {
          route: "/intern/functional-role",
          title: "Intern Functional Roles",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_FUNCTIONAL_ROLE_ALL",
        },
        {
          route: "/intern/leave-monthly-summary",
          title: "Intern Leave Monthly Summary",
          icon: <TableProperties size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_FUNCTIONAL_ROLE_ALL",
        },
        {
          route: "/intern/leave-type-monthly-summary",
          title: "Intern Leave Type Monthly Summary",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_FUNCTIONAL_ROLE_ALL",
        },
        {
          route: "/intern/leave-category-monthly-summary",
          title: "Intern Leave Category Monthly Summary",
          icon: <FolderTree size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_FUNCTIONAL_ROLE_ALL",
        },
        {
          route: "/intern/leave-balance-report",
          title: "Intern Leave Balance Report",
          icon: <FileBarChart2 size={16} strokeWidth={2} />,
          PermissionKey: "INTERN_FUNCTIONAL_ROLE_ALL",
        },

      ],
    },

    {
      heading: "Document Records",
      title: "Document Records",
      icon: <FileCheck size={19} strokeWidth={2} />,
      children: [
        {
          route: "/employee/documents",
          title: "Documents",
          icon: <FileText size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_DOCUMENT_VIEW_ALL",
        },
        // {
        //   route: "/employee/document/links",
        //   title: "Links",
        //   icon: <EditIcon />,
        // },
      ],
    },

    {
      heading: "Payroll Management",
      title: "Payroll Management",
      icon: <Wallet size={19} strokeWidth={2} />,
      children: [
        {
          route: "/payroll/cycles",
          title: "Payroll Cycles",
          icon: <CalendarClock size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_CYCLE_VIEW_ALL",
        },
        {
          route: "/payroll/component-types",
          title: "Payroll Component Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_COMPONENT_TYPE_VIEW_ALL",
        },

        {
          route: "/payroll/components",
          title: "Payroll Components",
          icon: <Component size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_COMPONENT_VIEW_ALL",
        },
        {
          route: "/payroll/component-slabs",
          title: "Payroll Component Slabs",
          icon: <TableProperties size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_COMPONENT_SLAB_VIEW_ALL",
        },

        {
          route: "/payroll/employee-salary-structure",
          title: "Payroll Salary Structures",
          icon: <Banknote size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_SALARY_VIEW_ALL",
        },
        {
          route: "/payroll/salary-structure/components",
          title: "Payroll Salary Structure Components",
          icon: <Wallet size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_SALARY_COMPONENT_VIEW_ALL",
        },
        {
          route: "/payroll/advance",
          title: "Payroll Advances",
          icon: <HandCoins size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_ADVANCE_VIEW_ALL",
        },
        {
          route: "/payroll/loan-types",
          title: "Payroll Loan Types",
          icon: <Landmark size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_LOAN_TYPE_VIEW_ALL",
        },
        {
          route: "/payroll/loans",
          title: "Payroll Loans",
          icon: <Landmark size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_LOAN_VIEW_ALL",
        },
        {
          route: "/payroll/loan-transactions",
          title: "Payroll Loan Transactions",
          icon: <Receipt size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_LOAN_TRANSACTION_VIEW_ALL",
        },
        {
          route: "/payroll/securities",
          title: "Payroll Securities",
          icon: <ShieldCheck size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_SECURITY_VIEW_ALL",
        },
        {
          route: "/payroll/securities-transactions",
          title: "Payroll Securities Transactions",
          icon: <ShieldEllipsis size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_SECURITY_TRANSACTION_VIEW_ALL",
        },
        {
          route: "/payroll/periods",
          title: "Payroll Periods",
          icon: <CalendarClock size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_PERIOD_VIEW_ALL",
        },
        {
          route: "/payroll/runs",
          title: "Payroll Runs",
          icon: <FileSpreadsheet size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_RUN_VIEW_ALL",
        },
        {
          route: "/payroll/employee-runs",
          title: "Payroll Employee Runs",
          icon: <UsersRound size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_EMPLOYEE_RUN_VIEW_ALL",
        },
        {
          route: "/payroll/employee-run/components",
          title: "Payroll Employee Run Components",
          icon: <Component size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_EMPLOYEE_RUN_COMPONENT_VIEW_ALL",
        },
        {
          route: "/payroll/reimbursements",
          title: "Payroll Reimbursements",
          icon: <Receipt size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_REIMBURSMENT_VIEW_ALL",
        },
        {
          route: "/payroll/reimbursement-types",
          title: "Payroll Reimbursement Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_REIMBURSMENT_TYPE_VIEW_ALL",
        },
        {
          route: "/payroll/adjustment-types",
          title: "Payroll Adjustment Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_ADJUSTMENT_TYPE_VIEW_ALL",
        },
        {
          route: "/payroll/adjustments",
          title: "Payroll Adjustments",
          icon: <NotebookPen size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_ADJUSTMENT_VIEW_ALL",
        },
        {
          route: "/payroll/journal-entries",
          title: "Payroll Journal Entries",
          icon: <ScrollText size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_JOURNAL_ENTRIES_VIEW_ALL",
        },
        {
          route: "/payroll/account-mapping",
          title: "Payroll Account Mappings",
          icon: <Network size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_ACCOUNT_MAPPING_VIEW_ALL",
        },

        {
          route: "/payroll/payslips",
          title: "Payslips",
          icon: <Receipt size={16} strokeWidth={2} />,
          PermissionKey: "PAYROLL_PAYSLIPS_VIEW_ALL",
        },
        {
          route: "/payslips/payments",
          title: "Payslip Payments",
          icon: <Banknote size={16} strokeWidth={2} />,
          PermissionKey: "PAYSLIP_PAYMENTS_VIEW_ALL",
        },
        {
          route: "/payslips/components",
          title: "Payslip Components",
          icon: <Wallet size={16} strokeWidth={2} />,
          PermissionKey: "PAYSLIP_COMPONENT_VIEW_ALL",
        },
      ],
    },

    {
      heading: "Time & Attendance",
      title: "Time & Attendance",
      icon: <Clock size={19} strokeWidth={2} />,
      children: [
        {
          route: "/organization/work-shift",
          title: "WorkShifts",
          icon: <Clock size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_VIEW_ALL",
        },

        {
          route: "/organization/work-shift-days",
          title: "WorkShift Days",
          icon: <CalendarClock size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_DAYS_ALL",
        },
        {
          route: "/organization/work-model-days",
          title: "WorkModel Days",
          icon: <CalendarClock size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_MODEL_DAYS_ALL",
        },

        {
          route: "/organization/work-shift-assignment",
          title: "Employee Shift Assignments",
          icon: <UsersRound size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_ASSIGNMENT_ALL",
        },

        {
          route: "/organization/work-shift-rotation-pattern",
          title: "Employee Shift Rotation Patterns",
          icon: <Network size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_ROTATION_PATTERN_ALL",
        },
        {
          route: "/organization/work-shift-rotation-days",
          title: "Employee Shift Rotation Days",
          icon: <CalendarCheck size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_ROTATION_DAYS_ALL",
        },
        {
          route: "/organization/work-shift-rotation-assignment",
          title: "Employee Shift Rotation Assignments",
          icon: <UsersRound size={16} strokeWidth={2} />,
          PermissionKey: "SHIFT_ROTATION_ASSIGNMENT_ALL",
        },
        {
          route: "/attendance/time-logs",
          title: "Attendance Time Logs",
          icon: <Timer size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_VIEW_ALL",
        },

        {
          route: "/attendance/employee-record",
          title: "Attendance Records",
          icon: <SquareCheckBig size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_RECORDS_ALL",
        },

        {
          route: "/attendance/employee-record-without-break",
          title: "Attendance Report Daily",
          icon: <FileBarChart2 size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_RECORD_WITHOUT_BREAK",
        },
        {
          route: "/attendance/monthly-report/employees",
          title: "Employee Monthly Attendance",
          icon: <FileSpreadsheet size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_RECORD_WITHOUT_BREAK",
        },
      ],
    },
    {
      heading: "Leave Management",
      title: "Leave Management",
      icon: <CalendarCheck size={19} strokeWidth={2} />,
      children: [
        {
          route: "/leave/holiday-calendar",
          title: "Holiday Calendar",
          icon: <CalendarCheck size={16} strokeWidth={2} />,
          PermissionKey: "HOLIDAY_CALENDAR_ALL",
        },
        {
          route: "/leave/employee-leaves",
          title: "Employee Leaves",
          icon: <NotebookPen size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_LEAVE_ALL",
        },
        {
          route: "/leave/employee-entitlements",
          title: "Employee Entitlements",
          icon: <BadgeCheck size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_ENTITLEMENT_ALL",
        },

        {
          route: "/employee-leave-summary",
          title: "Leave Summaries",
          icon: <TableProperties size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_SUMMARY_ALL",
        },
        {
          route: "/employee-leave-type-monthly-summary",
          title: "Leave Type Summaries",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_SUMMARY_ALL",
        },
        {
          route: "/employee-leave-category-monthly-summary",
          title: "Leave Category Summaries",
          icon: <FolderTree size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_SUMMARY_ALL",
        },


        {
          route: "/employee-leave-balance-report",
          title: "Leave Balance Report",
          icon: <FileBarChart2 size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_BALANCE_REPORT",
        },
      ],
    },
    {
      heading: "Salary Updates",
      title: "Salary Updates",
      icon: <TrendingUp size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Increment Types",
          route: "/employee/increment-types",
          title: "Increment Types",
          icon: <TrendingUp size={16} strokeWidth={2} />,
          PermissionKey: "INCREMENT_TYPE_ALL",
        },
        {
          route: "/employee/increment",
          title: "Increments",
          icon: <TrendingUp size={16} strokeWidth={2} />,
          PermissionKey: "INCREMENT_ALL",
        },
      ],
    },

    {
      heading: "Skill management",
      title: "Skill management",
      icon: <Award size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Skills Category",
          route: "/organization/skill-category",
          title: "Skills Category",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "SKILL_CATAGORY_ALL",
        },
        {
          heading: "Skills Element Groups",
          route: "/organization/skill-element-group",
          title: "Skills Element Group",
          icon: <FolderTree size={16} strokeWidth={2} />,
          PermissionKey: "SKILL_ELEMENT_GROUP_ALL",
        },
        {
          heading: "Functional Role Skills",
          route: "/organization/func-role-skill",
          title: "Functional Role Skill",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "FUNCTIONAL_ROLE_SKILL_ALL",
        },
        {
          heading: "Functional Role Skill Elements",
          route: "/organization/func-role-skill-element",
          title: "Functional Role Skill Elements",
          icon: <Star size={16} strokeWidth={2} />,
          PermissionKey: "FUNCTIONAL_ROLE_SKILL_ELEMENT_ALL",
        },
      ],
    },
    {
      heading: "Learning resources",
      title: "Learning resources",
      icon: <BookOpenText size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Learning Provider Types",
          route: "/organization/learning-provider-type",
          title: "Learning Provider Types",
          icon: <Building2 size={16} strokeWidth={2} />,
          PermissionKey: "LEARNING_PROVIDER_TYPE_ALL",
        },
        {
          heading: "Learning Provider",
          route: "/organization/learning-provider",
          title: "Learning Provider",
          icon: <Building size={16} strokeWidth={2} />,
          PermissionKey: "LEARNING_PROVIDER_ALL",
        },
        {
          heading: "Learning Resources",
          route: "/organization/learning-resources",
          title: "Learning Resources",
          icon: <BookOpen size={16} strokeWidth={2} />,
          PermissionKey: "LEARNING_RESOURCE_ALL",
        },
        {
          heading: "Learning Functional Role",
          route: "/organization/learning-functional-role",
          title: "Learning Functional Role",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "LEARNING_FUNCTIONAL_ROLE_ALL",
        },
        {
          heading: "Learning Skill Elements",
          route: "/organization/learning-skill-element",
          title: "Learning Skill Elements",
          icon: <Star size={16} strokeWidth={2} />,
          PermissionKey: "LEARNING_SKILL_ELEMENT_ALL",
        },
      ],
    },

    {
      heading: "Recruitment",
      title: "Recruitment",
      icon: <Network size={19} strokeWidth={2} />,
      children: [
        {
          route: "/recruitment/source-types",
          title: "Source Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "SOURCE_TYPES_VIEW",
        },
        {
          route: "/recruitment/sources",
          title: "Recruitment Sources",
          icon: <Briefcase size={16} strokeWidth={2} />,
          PermissionKey: "RECRUITMENT_SOURCES_VIEW",
        },
        {
          route: "/recruitment/source-contacts",
          title: "Source Contacts",
          icon: <Users size={16} strokeWidth={2} />,
          PermissionKey: "SOURCE_CONTACTS_VIEW",
        },
        {
          route: "/recruitment/openings",
          title: "Recruitment Openings",
          icon: <ClipboardList size={16} strokeWidth={2} />,
          PermissionKey: "RECRUITMENT_SOURCES_VIEW",
        },
        {
          route: "/recruitment/applications",
          title: "Applications",
          icon: <FileText size={16} strokeWidth={2} />,
          PermissionKey: "RECRUITMENT_SOURCES_VIEW",
        },
      ],
    },

    {
      heading: "Organization SetUp",
      title: "Organization SetUp",
      icon: <Building2 size={19} strokeWidth={2} />,
      children: [
        // {
        //   route: "/organization/data",
        //   title: "Organization",
        //   icon: <CorporateFareIcon />,
        // },
        {
          route: "/organization/details",
          title: "Organizations",
          icon: <Building2 size={16} strokeWidth={2} />,
          PermissionKey: "ORGANIZATION_ALL",
        },
        {
          route: "/organization/entity",
          title: "Organization Entities",
          icon: <Building2 size={16} strokeWidth={2} />,
          PermissionKey: "ENTITY_ALL",
        },
        {
          route: "/organization/profile",
          title: "Organization Profile",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "ORGANIZATION_PROFILE_ALL",
        },
        {
          route: "/organization/departments",
          title: "Departments",
          icon: <Briefcase size={16} strokeWidth={2} />,
          PermissionKey: "DEPARTMENT_ALL",
        },
        {
          route: "/organization/location",
          title: "Locations",
          icon: <MapPin size={16} strokeWidth={2} />,
          PermissionKey: "LOCATION_ALL",
        },
        {
          route: "/organization/application-access",
          title: "Application Access",
          icon: <KeyRound size={16} strokeWidth={2} />,
          PermissionKey: "VIEW_ORGANIZATION_APPLICATION",
        },

        {
          route: "/organization/units",
          title: "Organization Units",
          icon: <Grid2x2 size={16} strokeWidth={2} />,
          PermissionKey: "UNITS_ALL",
        },
        {
          route: "/organization/designation",
          title: "Designations",
          icon: <BadgeCheck size={16} strokeWidth={2} />,
          PermissionKey: "DESIGNATION_ALL",
        },
        {
          route: "/organization-configration/organization-level",
          title: "Organization Levels",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "",
        },
        {
          route: "/organization-configration/organization-hierarchy",
          title: "Organization Hierarchy",
          icon: <Network size={16} strokeWidth={2} />,
          PermissionKey: "",
        },
        {
          route: "/organization/registrations",
          title: "Registration",
          icon: <ClipboardCheck size={16} strokeWidth={2} />,
          PermissionKey: "REGISTRATION_ALL",
        },
        {
          route: "/attendance/status-type",
          title: "Attendance Status Types",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_STATUS_ALL",
        },
        {
          route: "/attendance/deviation-reason",
          title: "Attendance Deviation Reasons",
          icon: <AlertCircle size={16} strokeWidth={2} />,
          PermissionKey: "ATENDANCE_DEVIATION_REASON_ALL",
        },
        {
          route: "/attendance/break-type",
          title: "Attendance Break Types",
          icon: <Coffee size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_BREAK_TYPE_ALL",
        },
        {
          route: "/attendance/source",
          title: "Attendance Sources",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_SOURCE_ALL",
        },

        {
          route: "/organization/attendance-break",
          title: "Attendance Breaks",
          icon: <Coffee size={16} strokeWidth={2} />,
          PermissionKey: "ATTENDANCE_BREAK_ALL",
        },
        {
          route: "/organization/workshift-break",
          title: "Workshift Breaks",
          icon: <Coffee size={16} strokeWidth={2} />,
          PermissionKey: "WORKSHIFT_BREAK_ALL",
        },
        {
          route: "/organization/leave-policy",
          title: "Leave Policies",
          icon: <ScrollText size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_POLICY_ALL",
        },
        {
          route: "/organization/functional-roles",
          title: "Organization Functional Roles",
          icon: <BadgeInfo size={16} strokeWidth={2} />,
          PermissionKey: "FUNCTIONAL_ROLE_ALL",
        },
        {
          route: "/organization/functional-role-specialization",
          title: "Functional Role Specializations",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "FUNCTIONAL_ROLE_SPECIALIZATION",
        },
        {
          route: "/employee/document/types",
          title: "Document Types",
          icon: <FileText size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEE_DOCUMENT_TYPE_VIEW_ALL",
        },
      ],
    },

    {
      heading: "Organization Configuration",
      title: "Organization Configuration",
      icon: <SlidersHorizontal size={19} strokeWidth={2} />,
      children: [
        {
          route: "/organization-configration/business-ownership-type",
          title: "Business Ownership Types",
          icon: <Landmark size={16} strokeWidth={2} />,
          PermissionKey: "BUSINESS_OWNERSHIP_TYPE_ALL",
        },
        {
          route: "/organization-configration/business-registration-type",
          title: "Business Registration Types",
          icon: <ScrollText size={16} strokeWidth={2} />,
          PermissionKey: "BUSINESS_REGISTRATION_TYPE_ALL",
        },
        {
          route: "/organization-configration/unit-types",
          title: "Business Unit Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "BUSINESS_UNIT_TYPE_ALL",
        },
        {
          route: "/organization-configration/residential-ownership-type",
          title: "Residential Ownership Types",
          icon: <MapPin size={16} strokeWidth={2} />,
          PermissionKey: "RESIDENTIAL_OWNERSHIP_TYPE_ALL",
        },
        {
          route: "/organization-configration/location-ownership-type",
          title: "Location Ownership Types",
          icon: <MapPin size={16} strokeWidth={2} />,
          PermissionKey: "LOCATION_OWNERSHIP_TYPE_ALL",
        },
        {
          route: "/organization-configration/employee-address-types",
          title: "Address Types",
          icon: <MapPin size={16} strokeWidth={2} />,
          PermissionKey: "ADDRESS_TYPE_ALL",
        },
        {
          route: "/organization-configration/employee-status",
          title: "Employment Statuses",
          icon: <ListChecks size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYEMENT_STATUS_ALL",
        },
        {
          route: "/organization-configration/employement-type",
          title: "Employment Types",
          icon: <IdCard size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYMENT_TYPE_ALL",
        },
        {
          route: "/organization-configration/employement-exit-reason-type",
          title: "Exit Reason Types",
          icon: <DoorOpen size={16} strokeWidth={2} />,
          PermissionKey: "EXIT_REASON_TYPE",
        },
        {
          route: "/organization-configration/employement-category",
          title: "Employment Categories",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "EMPLOYMENT_CATEGORY_ALL",
        },

        {
          route: "/organization-configration/employement-exit-reason",
          title: "Exit Reasons",
          icon: <DoorOpen size={16} strokeWidth={2} />,
          PermissionKey: "EXIT_REASON_ALL",
        },
        {
          route: "/organization-configration/holiday-types",
          title: "Holiday Types",
          icon: <CalendarCheck size={16} strokeWidth={2} />,
          PermissionKey: "HOLIDAY_TYPE_ALL",
        },
        {
          route: "/organization-configration/leave-category",
          title: "Leave Categories",
          icon: <FolderTree size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_CATEGORY_ALL",
        },

        {
          route: "/organization-configration/leave-types",
          title: "Leave Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_TYPE_ALL",
        },
        {
          route: "/organization-configration/leave-reason-type",
          title: "Leave Reason Types",
          icon: <Layers size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_REASON_TYPE_ALL",
        },
        {
          route: "/organization-configration/leave-reason",
          title: "Leave Reasons",
          icon: <NotebookPen size={16} strokeWidth={2} />,
          PermissionKey: "LEAVE_REASON_ALL",
        },
        {
          route: "/organization-configration/workshift-types",
          title: "WorkShift Types",
          icon: <Clock size={16} strokeWidth={2} />,
          PermissionKey: "WORKSHIFT_TYPE_ALL",
        },
        {
          route: "/organization-configration/work-model",
          title: "Work Models",
          icon: <Briefcase size={16} strokeWidth={2} />,
          PermissionKey: "WORK_MODEL_ALL",
        },

        {
          route: "/organization-configration/person-identification-type",
          title: "Person Identification Types",
          icon: <IdCard size={16} strokeWidth={2} />,
          PermissionKey: "WORK_MODEL_ALL",
        },


        {
          route: "/organization-configration/user-types",
          title: "User Types",
          icon: <UsersRound size={16} strokeWidth={2} />,
          PermissionKey: "USER_TYPE_ALL",
        },
        {
          route: "/organization-configration/languages",
          title: "Languages",
          icon: <BookOpen size={16} strokeWidth={2} />,
          PermissionKey: "LANGUAGE_ALL",
        },
        {
          route: "/organization-configration/datagrid-config",
          title: "Data Grid Configuration",
          icon: <TableProperties size={16} strokeWidth={2} />,
          PermissionKey: "GRID_CONFIGRATION",
        },
      ],
    },

    {
      heading: "User management",
      title: "User management",
      icon: <UserCog size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Users",
          route: "/users",
          title: "Users",
          icon: <UsersRound size={16} strokeWidth={2} />,
          PermissionKey: "USER_VIEW_ALL",
        },
        {
          route: "/application/user-roles",
          title: "User Roles",
          icon: <ShieldCheck size={16} strokeWidth={2} />,
          PermissionKey: "USER_ROLE_ALL",
        },

        {
          route: "/application/user-role-permission",
          title: "User Role Permissions",
          icon: <ShieldEllipsis size={16} strokeWidth={2} />,
          PermissionKey: "USER_ROLE_PERMISSION_ALL",
        },
         {
          route: "/application/user-permission",
          title: "User Permission Overrides",
          icon: <KeySquare size={16} strokeWidth={2} />,
          PermissionKey: "USER_PERMISSION_ALL",
        },
        {
          route: "/application/activity-logs",
          title: "Activity Logs",
          icon: <ScrollText size={16} strokeWidth={2} />,
          PermissionKey: "VIEW_ACTIVITY_LOGS",
        },
      ],
    },

    {
      heading: "Tools & Utilities",
      title: "Tools & Utilities",
      icon: <Wrench size={19} strokeWidth={2} />,
      children: [
        {
          heading: "Tools & Utilities",
          route: "/organization/employee-shift",
          title: "Copy Employee",
          icon: <Copy size={16} strokeWidth={2} />,
          PermissionKey: "COPY_EMPLOYEE_ALL",
        },
      ],
    },

  ]


  const [sidebaroption, setSideBarOption] = useState(sidebarOptions);
  const [openGroups, setOpenGroups] = useState(() => {
    const o = {};
    sidebarOptions.forEach((item) => {
      if (item.children && item.children.some((c) => c.route === location.pathname)) {
        o[item.heading] = true;
      }
    });
    return o;
  });

  const routePermissionByPath = React.useMemo(() => {
    const map = {};
    for (const route of permissionRoutes) {
      if (route?.name && !map[route.name]) {
        map[route.name] = route.action;
      }
    }
    return map;
  }, []);

  const canAccessSidebarItem = React.useCallback(
    (child) => {
      if (
        child?.route === "/organization/details" ||
        child?.route === "/organization/switcher"
      ) {
        return canAccessOrganizationList(Permission);
      }

      const action =
        routePermissionByPath[child?.route] || child?.PermissionKey || "";
      if (!action) return true;
      return hasPermissionAction(Permission, action);
    },
    [Permission, routePermissionByPath]
  );

  useEffect(() => {
    if (Permission?.length > 0) {
      const filtered = sidebarOptions
        .map((item) => {
          // If item has children -> filter children
          if (item.children) {
            const allowedChildren = item.children.filter((child) =>
              canAccessSidebarItem(child)
            );

            // If no child is allowed → remove whole section
            if (allowedChildren.length === 0) return null;

            return { ...item, children: allowedChildren };
          }

          // If item has a PermissionKey → check
          if (item.PermissionKey) {
            const action =
              routePermissionByPath[item.route] || item.PermissionKey;
            return hasPermissionAction(Permission, action) ? item : null;
          }

          // Items without permissionKey (like Dashboard or dividers) always remain
          return item;
        })
        .filter(Boolean); // remove null items

      setSideBarOption(filtered);
    }
  }, [Permission, canAccessSidebarItem, routePermissionByPath]);

  const handleNav = (route) => navigate(route);

  const handleParentNavClick = (heading) => {
    if (collapsed) {
      onRequestExpand?.();
      setOpenGroups((o) => ({ ...o, [heading]: true }));
      return;
    }
    toggleGroup(heading);
  };

  const handleLeafNavClick = (route) => {
    if (collapsed) {
      onRequestExpand?.();
    }
    handleNav(route);
  };

  const toggleGroup = (heading) =>
    setOpenGroups((o) => ({ ...o, [heading]: !o[heading] }));

  let seenLabels = new Set();

  return (
    <>
      <div className="ems-sidebar-scroll">
        {sidebaroption?.map((item, index) => {
          if (item.kind === "divider" || item.kind === "header") return null;

          const sectionLabel = SECTION_BEFORE[item.heading];
          const showLabel = sectionLabel && !seenLabels.has(sectionLabel);
          if (showLabel) seenLabels.add(sectionLabel);

          const isActive = !item.children && item.route === location.pathname;
          const childActive = item.children && item.children.some((c) => c.route === location.pathname);
          const isOpen = !!openGroups[item.heading];

          return (
            <React.Fragment key={item.heading || index}>
              {showLabel && (
                <div className="ems-nav-section-label">{sectionLabel}</div>
              )}
              {item.children ? (
                <>
                  <button
                    className={[
                      "ems-nav-item",
                      childActive ? "parent-active" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleParentNavClick(item.heading)}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className="ems-ni-icon">{item.icon}</span>
                    <span className="ems-ni-label">{item.title}</span>
                    <span className={`ems-ni-chev${isOpen ? " open" : ""}`}>
                      <ChevronRight size={16} strokeWidth={2.4} />
                    </span>
                  </button>
                  {!collapsed && (
                    <NavChildren open={isOpen}>
                      {item.children.map((child) => (
                        <button
                          key={child.route}
                          className={[
                            "ems-nav-child",
                            location.pathname === child.route ? "active" : "",
                          ].filter(Boolean).join(" ")}
                          onClick={() => handleLeafNavClick(child.route)}
                        >
                          <span className="ems-nc-icon">{child.icon}</span>
                          <span className="ems-nc-label">{child.title}</span>
                        </button>
                      ))}
                    </NavChildren>
                  )}
                </>
              ) : (
                <button
                  className={[
                    "ems-nav-item",
                    isActive ? "active" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleLeafNavClick(item.route)}
                  title={collapsed ? item.title : undefined}
                >
                  <span className="ems-ni-icon">{item.icon}</span>
                  <span className="ems-ni-label">{item.title}</span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="ems-sidebar-foot">
        <button
          className="ems-nav-item"
          onClick={handleLogout}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="ems-ni-icon">
            <LogOut size={19} strokeWidth={2} />
          </span>
          <span className="ems-ni-label">Logout</span>
        </button>
      </div>
    </>
  );
}
